import { HttpResponse } from '@angular/common/http';
import { Injectable, Signal, signal } from '@angular/core';
import { SINGLE_QUOTES_REGEX } from '@common/constants';
import { BooleanOperator, ComparisonOperator, ServiceType } from '@common/enums';
import { DriveItemDetails, DriveOdataItem, DriveODataOption, RestoreDriveItems } from '@common/models';
import { ODataPagedResult } from '@common/odata';
import { fetchItemDetailsHelper } from '@common/odata/utils';
import { SharedOdataService } from '@common/services';
import { DriveBaseTag, DriveBaseTagsMap } from '@common/services/smart-search/drive-base/smart-search-tags-drive-base.model';
import { filterByWords } from '@common/utils';
import { containsWrapper } from '@common/utils/functions/search';
import { SmartSearchHelper } from '@common/utils/helper/smart-search.helper';
import { SmartSearchModel } from 'mbs-ui-kit';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export type ICollectionAction = { ServiceType: ServiceType; Password: string; Ids: string[] };
export type ISiteCollectionAction = { Password: string; Ids: string[] };

@Injectable({ providedIn: 'root' })
export class DriveOdataService extends SharedOdataService<DriveOdataItem> {
  private readonly odataMethods = this.odataFactory.CreateService<any>('');
  private readonly odataDriveItems = this.odataFactory.CreateService<any>('DriveItems');

  readonly #fileName = DriveBaseTagsMap.get(DriveBaseTag.File);
  readonly #folderName = DriveBaseTagsMap.get(DriveBaseTag.Folder);
  readonly #name = DriveBaseTagsMap.get(DriveBaseTag.Name);

  readonly #fileType = `${this.#fileName.prop} ${ComparisonOperator.Eq} true`;
  readonly #folderType = `${this.#folderName.prop} ${ComparisonOperator.Eq} false`;

  readonly #dateTime = signal<string>(null);

  get dateTime(): Signal<string> {
    return this.#dateTime.asReadonly();
  }

  set dateTime(value: string) {
    this.#dateTime.set(value);
    this.updateCustomOptions({ key: 'dateTime', value }, !value);
  }

  constructor() {
    super('DriveItems');
  }

  fetchItems(): Observable<ODataPagedResult<DriveOdataItem>> {
    return this.fetchData(this.odataDriveItems);
  }

  getPage(id: string): Observable<number> {
    const newOdata = this.odataFactory.CreateService(`/DriveItems/GetPage`);

    return newOdata
      .Query()
      .Top(this.pageSize)
      .OrderBy(this.orderBy)
      .CustomQueryOptions([
        { key: 'Id', value: id },
        { key: 'TeamDrive', value: this.customQueryOptions.get(DriveODataOption.TeamDrive) }
      ])
      .Exec() as unknown as Observable<number>;
  }

  restoreDriveItems(data: RestoreDriveItems): Observable<RestoreDriveItems> {
    return this.odataMethods
      .CustomCollectionAction('RestoreDriveItems', data)
      .pipe(map((res: HttpResponse<RestoreDriveItems>) => res.body));
  }

  getItemDetails(id: string): Observable<DriveItemDetails> {
    const dateTime = this.dateTime();

    return fetchItemDetailsHelper(this.odataDriveItems, { id, dateTime });
  }

  deleteDriveItems(data: ICollectionAction): Observable<ICollectionAction> {
    return this.odataMethods
      .CustomCollectionAction('DeleteDriveItems', { ...data, ServiceType: ServiceType[data.ServiceType] })
      .pipe(map((res: HttpResponse<ICollectionAction>) => res.body));
  }

  deleteSiteBlocks(data: ISiteCollectionAction): Observable<ISiteCollectionAction> {
    return this.odataMethods.CustomCollectionAction('DeleteSiteBlocks', data).pipe(map((res: HttpResponse<ICollectionAction>) => res.body));
  }

  deleteSiteBlockItems(data: ISiteCollectionAction): Observable<ISiteCollectionAction> {
    return this.odataMethods
      .CustomCollectionAction('DeleteSiteBlockItems', data)
      .pipe(map((res: HttpResponse<ICollectionAction>) => res.body));
  }

  updateFilter(searchObj: SmartSearchModel): void {
    const filter: string[] = [];
    const values = SmartSearchHelper.getValues(searchObj);

    if (searchObj[this.#fileName.tag] && !searchObj[this.#folderName.tag]) {
      const value = SmartSearchHelper.getJoinedValue(values, this.#fileName.tag);

      filter.push(`${this.#fileType} ${BooleanOperator.And} ${containsWrapper(this.#name.prop, value)}`);
    }

    if (searchObj[this.#folderName.tag] && !searchObj[this.#fileName.tag]) {
      const value = SmartSearchHelper.getJoinedValue(values, this.#folderName.tag);

      filter.push(`${this.#folderType} ${BooleanOperator.And} ${containsWrapper(this.#name.prop, value)}`);
    }

    if (searchObj[this.#fileName.tag] && searchObj[this.#folderName.tag]) {
      const fileValue = SmartSearchHelper.getJoinedValue(values, this.#fileName.tag);
      const folderValue = SmartSearchHelper.getJoinedValue(values, this.#folderName.tag);
      const fileCondition = `${this.#fileType} ${BooleanOperator.And} ${containsWrapper(this.#name.prop, fileValue)}`;
      const folderCondition = `${this.#folderType} ${BooleanOperator.And} ${containsWrapper(this.#name.prop, folderValue)}`;

      filter.push(`(${fileCondition}) ${BooleanOperator.Or} (${folderCondition})`);
    }

    if (searchObj.words?.filter(Boolean)) {
      const term = filterByWords(searchObj).replace(SINGLE_QUOTES_REGEX, '');
      const keys = [this.#name.prop];
      const values = keys.map((k) => containsWrapper(k, term));

      filter.push('('.concat(values.join(` ${BooleanOperator.Or} `), ')'));
    }

    this.filter = filter.length > 0 ? filter.join(` ${BooleanOperator.And} `) : '';
  }

  updateCustomOptions(options: { key: string; value?: any }, isDelete = false): void {
    const { key, value } = options || {};

    if (isDelete) {
      this.removeCustomQueryOption(key);
    } else {
      this.addCustomQueryOption(key, value);
    }
  }

  setDefaultOdataOptions(): void {
    this.page = 1;
    this.pageSize = 50;
    this.orderBy = 'Type asc, Name asc';
  }
}
